import { ESAT_OPTIONAL_ORDER, normalizeEsatHeritageExam } from './esatModulePicker';
import {
  esatStatusFromDbPapers,
  formatPaperRaw,
  sittingScoreFromEsatModules,
} from './esatScore';

export const ESAT_OPTIONAL_MODULE_ABBREVIATIONS = {
  biology: 'Bio',
  chemistry: 'Chem',
  physics: 'Phys',
  mathematics_2: 'M2',
};

export function esatSittingTableHeaders(heritageExam) {
  const heritage = normalizeEsatHeritageExam(heritageExam);
  if (heritage === 'ENGAA') return { first: 'S1A', second: 'Sections' };
  if (heritage === 'NSAA') return { first: 'Mathematics', second: 'Optional modules' };
  return { first: 'Mathematics 1', second: 'Optional modules' };
}

export function engaaSectionChipLabel(paper, moduleKey) {
  const hay = `${paper?.display_name || ''} ${paper?.title || ''} ${paper?.id || ''}`.toLowerCase();
  if (/\bsection\s*2\b|\bs2\b/.test(hay)) return 'S2';
  if (/\bpart\s*b\b|\bsection\s*b\b|\bs1b\b/.test(hay)) return 'S1B';
  if (/\bpart\s*a\b|\bsection\s*a\b|\bs1a\b/.test(hay)) return 'S1A';
  if (moduleKey === 'mathematics_1') return 'S1A';
  if (moduleKey === 'physics') return 'S1B';
  return 'S2';
}

export function esatOptionalChipLabel(key, heritageExam) {
  const heritage = normalizeEsatHeritageExam(heritageExam);
  if (heritage === 'NSAA' && key === 'mathematics_2') return 'AMP';
  return ESAT_OPTIONAL_MODULE_ABBREVIATIONS[key] || key;
}

function paperHasAttempt(id, scoresMap, dbPapers) {
  if (!id) return false;
  const dbStatus = dbPapers?.[String(id)]?.status;
  if (dbStatus === 'in_progress' || dbStatus === 'completed') return true;
  const score = scoresMap?.[String(id)];
  return Boolean(score && score.total > 0);
}

function catalogOptionalEntries(sitting) {
  return ESAT_OPTIONAL_ORDER
    .map((key) => {
      const paper = sitting?.optionals?.[key];
      const id = paper?.id;
      return id ? { key, id } : null;
    })
    .filter(Boolean);
}

/**
 * @returns {{ sat: boolean, moduleIds: {key,id}[] }}
 * moduleIds always starts with mathematics_1 when sat; includes chosen/locked others.
 */
export function resolveEsatAttemptModules({
  sitting,
  scoresMap = {},
  dbPapers = {},
  storedOptionalKeys = [],
} = {}) {
  const heritage = normalizeEsatHeritageExam(sitting?.heritageExam);
  const stored = [...new Set((storedOptionalKeys || []).filter(Boolean).map(String))];
  const maths1Id = sitting?.mathematics1?.id || sitting?.paper1?.id;
  const catalog = catalogOptionalEntries(sitting);
  const catalogByKey = new Map(catalog.map((m) => [m.key, m]));

  const sittingPaperIds = [
    maths1Id,
    ...catalog.map((m) => m.id),
  ].filter(Boolean);

  const inferredOptionals = catalog.filter((m) => paperHasAttempt(m.id, scoresMap, dbPapers));
  const anySittingAttempt = sittingPaperIds.some((id) => paperHasAttempt(id, scoresMap, dbPapers));

  let sat = stored.length > 0;
  if (!sat && heritage === 'ENGAA' && anySittingAttempt) sat = true;
  if (!sat && heritage !== 'ENGAA' && inferredOptionals.length > 0) sat = true;

  if (!sat) return { sat: false, moduleIds: [] };

  let further = [];
  if (heritage === 'ENGAA') {
    const storedInCatalog = stored
      .map((key) => catalogByKey.get(key))
      .filter(Boolean);
    further = storedInCatalog.length ? storedInCatalog : catalog;
  } else if (stored.length) {
    further = stored.map((key) => catalogByKey.get(key)).filter(Boolean);
  } else {
    further = inferredOptionals;
  }

  const moduleIds = [];
  if (maths1Id) moduleIds.push({ key: 'mathematics_1', id: maths1Id });
  further.forEach((m) => {
    if (!m?.id) return;
    if (moduleIds.some((row) => row.key === m.key || row.id === m.id)) return;
    moduleIds.push({ key: m.key, id: m.id });
  });

  return { sat: true, moduleIds };
}

export function esatWorkspaceRowStatus({ sat, moduleIds, scoresMap, dbPapers }) {
  if (!sat) return 'none';
  const fromDb = esatStatusFromDbPapers((moduleIds || []).map((m) => m.id), dbPapers);
  if (fromDb === 'done') return 'done';
  if (fromDb === 'progress') return 'progress';
  const scored = sittingScoreFromEsatModules(moduleIds, scoresMap);
  if (scored.status === 'done') return 'done';
  return 'progress';
}

function engaaChipRank(label) {
  if (label === 'S1B') return 0;
  if (label === 'S2') return 1;
  return 2;
}

export function esatFurtherColumnChips({
  sitting,
  moduleIds = [],
  scoresMap = {},
  heritageExam,
} = {}) {
  const heritage = normalizeEsatHeritageExam(heritageExam ?? sitting?.heritageExam);
  const further = (moduleIds || []).filter((m) => m.key !== 'mathematics_1' && m.id);

  if (heritage === 'ENGAA') {
    return further
      .map((m) => {
        const paper = sitting?.optionals?.[m.key];
        const score = scoresMap[String(m.id)];
        return {
          key: m.key,
          label: engaaSectionChipLabel(paper, m.key),
          scoreText: formatPaperRaw(score?.correct, score?.total),
        };
      })
      .sort((a, b) => engaaChipRank(a.label) - engaaChipRank(b.label));
  }

  return ESAT_OPTIONAL_ORDER
    .map((key) => further.find((m) => m.key === key))
    .filter(Boolean)
    .map((m) => {
      const score = scoresMap[String(m.id)];
      return {
        key: m.key,
        label: esatOptionalChipLabel(m.key, heritage),
        scoreText: formatPaperRaw(score?.correct, score?.total),
      };
    });
}

export function esatOptionalChipsAriaLabel(chips, sat) {
  if (!sat) return '—';
  if (!chips?.length) return '—';
  return chips.map((c) => `${c.label} ${c.scoreText}`).join(', ');
}
